/**
 * Clipboard write + the brief "copied" acknowledgement that follows it.
 *
 * The LOGIC is shared; the presentation is not. A compact glyph in a stats
 * table and a labelled button under a game's hero want different markup, so
 * this hook deliberately returns state rather than rendering anything.
 */
import { useState } from 'react';

export interface CopyToClipboard {
  /** True for {@link ACK_MS} after a successful write. */
  copied: boolean;
  copy: (value: string) => Promise<void>;
}

/** Long enough to read, short enough that the button is ready again quickly. */
export const ACK_MS = 1_500;

export function useCopyToClipboard(): CopyToClipboard {
  const [copied, setCopied] = useState(false);
  const copy = async (value: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), ACK_MS);
    } catch {
      // Clipboard permission denied, or no secure context. Nothing was copied,
      // so the acknowledgement must NOT claim otherwise.
      setCopied(false);
    }
  };
  return { copied, copy };
}
